import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowLeft, Plus, X } from "lucide-react";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import EligibleEventPicker from "../../components/disputes/EligibleEventPicker";
import { getClaimTypes, addClaim, addClaimProof, respondToClaim, uploadDisputeProof } from "../../services/disputeService";
import { color, radius } from "../../styles/theme";

const MAX_PROOF_FILES = 3;

/**
 * New Dispute / Respond to Dispute — one component, route-param driven, same
 * pattern as CreateEvent.jsx's isEdit. Ported from mobile's add-dispute
 * screen (src/Screens/add-dispute); the event/gallery picker is inline here
 * (EligibleEventPicker) instead of mobile's separate PaidEvents screen.
 */
const DisputeForm = () => {
  const { t } = useTranslation(["disputes", "common"]);
  const { claimId } = useParams();
  const isRespond = Boolean(claimId);
  const navigate = useNavigate();

  const [claimTypes, setClaimTypes] = useState([]);
  const [claimTypeId, setClaimTypeId] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [reason, setReason] = useState("");
  const [proofUrls, setProofUrls] = useState([]);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isRespond) return;
    getClaimTypes()
      .then((res) => setClaimTypes(res.data?.Result || []))
      .catch(() => toast.error(t("disputeForm.loadClaimTypesFailed")));
  }, [isRespond, t]);

  const handleProofFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const remaining = MAX_PROOF_FILES - proofUrls.length;
    if (remaining <= 0) {
      toast.warning(t("disputeForm.maxFilesWarning", { max: MAX_PROOF_FILES }));
      return;
    }
    const toUpload = files.slice(0, remaining);
    setUploadingProof(true);
    try {
      const urls = await uploadDisputeProof(toUpload);
      if (urls.length) setProofUrls((prev) => [...prev, ...urls]);
      else toast.error(t("disputeForm.uploadFailed"));
    } catch {
      toast.error(t("disputeForm.uploadFailed"));
    } finally {
      setUploadingProof(false);
    }
  };

  const removeProof = (url) => setProofUrls((prev) => prev.filter((u) => u !== url));

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error(isRespond ? t("disputeForm.enterResponse") : t("disputeForm.enterReason"));
      return;
    }

    setSubmitting(true);
    try {
      if (isRespond) {
        const res = await respondToClaim({ claimId, response: reason });
        toast.success(res.data?.Message || t("disputeForm.responseSubmittedSuccess"));
        navigate("/disputes");
        return;
      }

      if (!selectedEvent) {
        toast.error(t("disputeForm.selectEventFirst"));
        setSubmitting(false);
        return;
      }
      if (!claimTypeId) {
        toast.error(t("disputeForm.selectClaimType"));
        setSubmitting(false);
        return;
      }

      const res = await addClaim({
        eventId: selectedEvent.EventId,
        eventGalleryId: selectedEvent.EventGalleryId,
        albumId: selectedEvent.AlbumId ?? null,
        ClaimTypeId: claimTypeId,
        description: reason,
      });

      const newClaimId = res.data?.Result?.Id;
      if (newClaimId && proofUrls.length) {
        await addClaimProof(newClaimId, proofUrls).catch(() => toast.warning(t("disputeForm.proofAttachFailed")));
      }

      toast.success(res.data?.Message || t("disputeForm.disputeFiledSuccess"));
      navigate("/disputes");
    } catch (err) {
      toast.error(err.response?.data?.Message || t("disputeForm.submitFailed"));
      setSubmitting(false);
    }
  };

  return (
    <div>
      <button type="button" className="btn btn-ghost btn-sm" style={s.linkBtn} onClick={() => navigate("/disputes")}>
        <ArrowLeft size={13} strokeWidth={2.25} /> {t("disputeForm.backToDisputes")}
      </button>
      <h2 style={s.title}>{isRespond ? t("disputeForm.respondTitle") : t("disputeForm.newDisputeTitle")}</h2>

      <Card style={s.wrap}>
        {!isRespond && (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={s.fieldLabel}>{t("disputeForm.paidEventGalleryLabel")}</label>
              <button type="button" className="input" style={s.pickerBtn} onClick={() => setShowPicker(true)}>
                {selectedEvent
                  ? `${selectedEvent.EventName} — ${selectedEvent.AlbumName ?? selectedEvent.GalleryName}`
                  : t("disputeForm.selectEventPlaceholder")}
              </button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={s.fieldLabel}>{t("disputeForm.claimTypeLabel")}</label>
              <select className="input" value={claimTypeId} onChange={(e) => setClaimTypeId(e.target.value)}>
                <option value="">{t("disputeForm.selectOption")}</option>
                {claimTypes.map((ct) => <option key={ct.Id} value={ct.Id}>{ct.Title}</option>)}
              </select>
            </div>
          </>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={s.fieldLabel}>{isRespond ? t("disputeForm.yourResponseLabel") : t("disputeForm.reasonLabel")}</label>
          <textarea
            className="input"
            style={s.textarea}
            rows={5}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={isRespond ? t("disputeForm.responsePlaceholder") : t("disputeForm.reasonPlaceholder")}
          />
        </div>

        {!isRespond && (
          <div style={{ marginBottom: 16 }}>
            <label style={s.fieldLabel}>{t("disputeForm.proofLabel", { count: proofUrls.length, max: MAX_PROOF_FILES })}</label>
            <div style={s.proofRow}>
              {proofUrls.map((url) => (
                <div key={url} style={s.proofThumbWrap}>
                  <img src={url} alt="" style={s.proofThumb} />
                  <button type="button" style={s.removeBtn} onClick={() => removeProof(url)} title={t("disputeForm.removeProof")}><X size={11} strokeWidth={2.5} /></button>
                </div>
              ))}
            </div>
            <label className="btn btn-secondary btn-sm" style={{ ...s.uploadBtn, ...(uploadingProof || proofUrls.length >= MAX_PROOF_FILES ? { opacity: 0.5, pointerEvents: "none" } : {}) }}>
              <Plus size={13} strokeWidth={2.5} /> {uploadingProof ? t("disputeForm.uploading") : t("disputeForm.addProofImage")}
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => { handleProofFiles(e.target.files); e.target.value = ""; }}
                disabled={uploadingProof || proofUrls.length >= MAX_PROOF_FILES}
              />
            </label>
          </div>
        )}

        <Button onClick={handleSubmit} disabled={submitting || uploadingProof} style={{ marginTop: 8 }}>
          {submitting ? t("disputeForm.submitting") : isRespond ? t("disputeForm.submitResponse") : t("disputeForm.submitDispute")}
        </Button>
      </Card>

      {showPicker && (
        <EligibleEventPicker
          onClose={() => setShowPicker(false)}
          onSelect={(item) => { setSelectedEvent(item); setShowPicker(false); }}
        />
      )}
    </div>
  );
};

const s = {
  linkBtn: { color: color.gray600, fontWeight: 600, marginBottom: 8, padding: "6px 10px" },
  title: { fontSize: 19, fontWeight: 800, color: color.textPrimary, marginBottom: 18, letterSpacing: -0.3 },
  wrap: { maxWidth: 480, padding: 24 },
  fieldLabel: { display: "block", marginBottom: 6, fontWeight: 600, fontSize: 13, color: color.gray700 },
  pickerBtn: { textAlign: "left", cursor: "pointer", color: color.gray800 },
  textarea: { resize: "vertical", fontFamily: "inherit" },
  proofRow: { display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" },
  proofThumbWrap: { position: "relative", width: 72, height: 72, borderRadius: radius.sm, overflow: "hidden", background: color.gray100 },
  proofThumb: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  removeBtn: {
    position: "absolute", top: 3, right: 3, width: 18, height: 18, borderRadius: "50%",
    border: "none", background: "rgba(20,21,40,0.65)", color: "#fff", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  uploadBtn: { display: "inline-flex" },
};

export default DisputeForm;
